import { useState, useEffect } from 'react';
import type { Asset } from '@/types/asset';
import type { DisposalDetails, DisposeOfMode, DepreciationHandling } from '@/types/disposal';
import type { DisposalReason } from '@/types/settings';
import { dataService } from '@/services/data-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Info } from 'lucide-react';
import { format } from 'date-fns';

interface DisposalDetailsStepProps {
  asset: Asset;
  details: DisposalDetails;
  onDetailsChange: (details: DisposalDetails) => void;
  onNext: () => void;
  onCancel: () => void;
}

export function DisposalDetailsStep({
  asset,
  details,
  onDetailsChange,
  onNext,
  onCancel,
}: DisposalDetailsStepProps): React.ReactElement {
  const [disposalReasons, setDisposalReasons] = useState<DisposalReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasCurrentPeriodDepreciation, setHasCurrentPeriodDepreciation] = useState(false);

  useEffect(() => {
    loadDisposalReasons();
    // In a real implementation, check if there's current period depreciation
    // For now, we'll simulate this
    setHasCurrentPeriodDepreciation(false);
  }, []);

  const loadDisposalReasons = async () => {
    try {
      const reasons = await dataService.getDisposalReasons();
      setDisposalReasons(reasons);
    } catch (error) {
      console.error('Failed to load disposal reasons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof DisposalDetails, value: any) => {
    onDetailsChange({ ...details, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleDateChange = (date: string) => {
    // In a real implementation, calculate year and period from the date
    // using the organisation's accounting calendar
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const period = dateObj.getMonth() + 1;

    onDetailsChange({
      ...details,
      date,
      year,
      period,
    });

    if (errors.date) {
      setErrors({ ...errors, date: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!details.reference.trim()) {
      newErrors.reference = 'Reference is required';
    } else if (details.reference.length > 60) {
      newErrors.reference = 'Reference must be 60 characters or less';
    }

    if (!details.date) {
      newErrors.date = 'Date is required';
    }

    if (!details.disposalReasonId) {
      newErrors.disposalReasonId = 'Disposal reason is required';
    }

    if (!details.disposeOfMode) {
      newErrors.disposeOfMode = 'Dispose of mode is required';
    }

    if (details.disposeOfMode === 'quantity') {
      if (!details.disposalQuantity || details.disposalQuantity <= 0) {
        newErrors.disposalQuantity = 'Disposal quantity must be greater than 0';
      } else if (details.disposalQuantity > details.currentQuantity) {
        newErrors.disposalQuantity = 'Cannot exceed current quantity';
      }
    }

    if (hasCurrentPeriodDepreciation && !details.depreciationHandling) {
      newErrors.depreciationHandling = 'Please select how to handle current period depreciation';
    }

    if (details.notes && details.notes.length > 500) {
      newErrors.notes = 'Notes must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const isValueDisabled = hasCurrentPeriodDepreciation;

  return (
    <div className="space-y-6">
      {/* Transaction Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
        {/* Reference and Date */}
        <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reference">
            Reference <span className="text-destructive">*</span>
          </Label>
          <Input
            id="reference"
            placeholder="Enter a transaction reference"
            value={details.reference}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFieldChange('reference', e.target.value)}
            maxLength={60}
            className={errors.reference ? 'border-destructive' : ''}
          />
          {errors.reference && (
            <p className="text-sm text-destructive">{errors.reference}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="date">
            Date <span className="text-destructive">*</span>
          </Label>
          <Input
            id="date"
            type="date"
            value={details.date}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleDateChange(e.target.value)}
            className={errors.date ? 'border-destructive' : ''}
          />
          {errors.date && (
            <p className="text-sm text-destructive">{errors.date}</p>
          )}
        </div>
      </div>

      {/* Disposal Reason */}
      <div className="space-y-2">
        <Label htmlFor="disposalReason">
          Disposal Reason <span className="text-destructive">*</span>
        </Label>
        <Select
          value={details.disposalReasonId}
          onValueChange={(value: string) => handleFieldChange('disposalReasonId', value)}
        >
          <SelectTrigger id="disposalReason" className={errors.disposalReasonId ? 'border-destructive' : ''}>
            <SelectValue placeholder="Select a disposal reason" />
          </SelectTrigger>
          <SelectContent>
            {disposalReasons.map((reason) => (
              <SelectItem key={reason.id} value={reason.id}>
                {reason.code} - {reason.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.disposalReasonId && (
          <p className="text-sm text-destructive">{errors.disposalReasonId}</p>
        )}
      </div>

      {/* Year and Period (Read-only) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="year">Year</Label>
          <Input
            id="year"
            value={details.year}
            readOnly
            className="bg-muted"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="period">Period</Label>
          <Input
            id="period"
            value={details.period}
            readOnly
            className="bg-muted"
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Optional notes (max 500 characters)"
          value={details.notes || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFieldChange('notes', e.target.value)}
          maxLength={500}
          rows={3}
          className={errors.notes ? 'border-destructive' : ''}
        />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes}</p>
        )}
        <p className="text-sm text-muted-foreground">
          {(details.notes || '').length}/500 characters
        </p>
      </div>

        </CardContent>
      </Card>

      {/* Disposal Method Card */}
      <Card>
        <CardHeader>
          <CardTitle>Disposal Method</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
        {/* Dispose Of */}
        <div className="space-y-2">
          <Label>
            Dispose Of <span className="text-destructive">*</span>
          </Label>
          <RadioGroup
            value={details.disposeOfMode}
            onValueChange={(value: string) => handleFieldChange('disposeOfMode', value as DisposeOfMode)}
            className="flex flex-row gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="value" id="value" disabled={isValueDisabled} />
              <Label htmlFor="value" className={isValueDisabled ? 'text-muted-foreground' : ''}>
                Value
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="percentage" id="percentage" />
              <Label htmlFor="percentage">Percentage</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="quantity" id="quantity" />
              <Label htmlFor="quantity">Quantity</Label>
            </div>
          </RadioGroup>
          {isValueDisabled && (
            <p className="text-sm text-muted-foreground">
              Dispose of 'Value' is unavailable when depreciation has been posted this period.
            </p>
          )}
          {errors.disposeOfMode && (
            <p className="text-sm text-destructive">{errors.disposeOfMode}</p>
          )}
        </div>

        {/* Current Quantity and Disposal Quantity */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="currentQuantity">Current Quantity</Label>
            <Input
              id="currentQuantity"
              type="number"
              value={details.currentQuantity}
              readOnly
              className="bg-muted"
            />
          </div>

          {details.disposeOfMode === 'quantity' && (
            <div className="space-y-2">
              <Label htmlFor="disposalQuantity">
                Disposal Quantity <span className="text-destructive">*</span>
              </Label>
              <Input
                id="disposalQuantity"
                type="number"
                step="0.0001"
                min="0.0001"
                max={details.currentQuantity}
                value={details.disposalQuantity || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFieldChange('disposalQuantity', parseFloat(e.target.value) || 0)}
                className={errors.disposalQuantity ? 'border-destructive' : ''}
              />
              {errors.disposalQuantity && (
                <p className="text-sm text-destructive">{errors.disposalQuantity}</p>
              )}
            </div>
          )}
        </div>
        </CardContent>
      </Card>

      {/* Conditional Depreciation Notice */}
      {hasCurrentPeriodDepreciation && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-3">
              <p>
                Depreciation has been posted in the current period for this asset. Please select whether this depreciation should be reversed or disposed of.
              </p>
              <RadioGroup
                value={details.depreciationHandling}
                onValueChange={(value: string) => handleFieldChange('depreciationHandling', value as DepreciationHandling)}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reverse" id="reverse" />
                  <Label htmlFor="reverse">Reverse current period depreciation</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dispose" id="dispose" />
                  <Label htmlFor="dispose">Dispose current period depreciation</Label>
                </div>
              </RadioGroup>
              {errors.depreciationHandling && (
                <p className="text-sm text-destructive">{errors.depreciationHandling}</p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
